import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { CommercialCheckoutCompleteResult } from "@itaas/pep-contracts";
import { getCheckoutResult } from "../api/pep-api";
import { SiteLayout } from "../components/layout/SiteLayout";
import { SeoHead } from "../components/seo/SeoHead";
import { SUITE_URL } from "../config";
import { buildSuiteLoginUrl } from "../utils/trial-redirect";

export function TrialSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") ?? params.get("token") ?? "";
  const provider = params.get("provider") ?? "stripe";
  const [result, setResult] = useState<CommercialCheckoutCompleteResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!sessionId) {
      setError("Session de paiement invalide.");
      setLoading(false);
      return;
    }

    let attempts = 0;
    const poll = () => {
      getCheckoutResult(sessionId)
        .then((data) => {
          setResult(data);
          setLoading(false);
        })
        .catch(() => {
          attempts += 1;
          if (attempts < 8) {
            window.setTimeout(poll, 2000);
          } else {
            setError(
              "Paiement reçu — activation en cours. Vérifiez votre email ou contactez le support.",
            );
            setLoading(false);
          }
        });
    };
    poll();
  }, [sessionId]);

  return (
    <SiteLayout>
      <SeoHead
        title="Paiement confirmé"
        description="Confirmation de paiement et activation du compte essai ITaaS."
        canonicalPath="/trial/success"
        noIndex
      />
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-7">
            {loading && (
              <div className="card border-0 shadow-sm p-5 text-center">
                <div className="spinner-border text-primary mb-3" role="status" />
                <p className="mb-0">
                  Activation de votre compte après paiement{" "}
                  {provider === "paypal" ? "PayPal" : "Stripe"}…
                </p>
              </div>
            )}

            {!loading && error && (
              <div className="alert alert-warning">
                {error}
                <div className="mt-3">
                  <Link to="/contact">Contacter le support</Link>
                </div>
              </div>
            )}

            {result && (
              <div className="card border-0 shadow-sm p-4 p-md-5">
                <div className="text-center mb-4">
                  <div className="display-6 text-success mb-2">✓</div>
                  <h1 className="h4 fw-bold">Paiement confirmé</h1>
                  <p className="text-secondary mb-0">Votre organisation est active.</p>
                </div>
                <dl className="row small mb-4">
                  <dt className="col-sm-4">Organisation</dt>
                  <dd className="col-sm-8 font-monospace">{result.tenantSlug}</dd>
                  <dt className="col-sm-4">Email de connexion</dt>
                  <dd className="col-sm-8 font-monospace">{result.adminEmail}</dd>
                  {result.contactEmail && result.contactEmail !== result.adminEmail && (
                    <>
                      <dt className="col-sm-4">Email contact</dt>
                      <dd className="col-sm-8">{result.contactEmail}</dd>
                    </>
                  )}
                  <dt className="col-sm-4">Mot de passe temporaire</dt>
                  <dd className="col-sm-8 font-monospace">{result.temporaryPassword}</dd>
                </dl>
                {result.contactEmail && (
                  <p className="small text-secondary text-center mb-3">
                    Un récapitulatif a été envoyé à <strong>{result.contactEmail}</strong>.
                  </p>
                )}
                <p className="small text-secondary">
                  Conservez ces identifiants et changez le mot de passe à la première connexion.
                </p>
                <a
                  href={buildSuiteLoginUrl(SUITE_URL, {
                    tenantSlug: result.tenantSlug,
                    adminEmail: result.adminEmail,
                    module: result.modules[0],
                  })}
                  className="btn btn-primary btn-lg w-100"
                >
                  Ouvrir la Suite ITaaS →
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}
